export const PAGE_URL_FILTER_PARAMS = {
  search: 'q',
  status: 'status',
  category: 'category',
  type: 'type',
  sort: 'sort',
  installation: 'install',
  provenance: 'source',
  page: 'page',
} as const;

export interface PageUrlFilterCodec<State> {
  read: (searchParams: URLSearchParams) => State;
  write: (searchParams: URLSearchParams, state: State) => void;
}

export function writePageUrlFilterState<State>(
  currentSearchParams: URLSearchParams,
  state: State,
  codec: PageUrlFilterCodec<State>
): URLSearchParams {
  const nextSearchParams = new URLSearchParams(currentSearchParams);
  codec.write(nextSearchParams, state);
  return nextSearchParams;
}

function setDefaultedParam(
  searchParams: URLSearchParams,
  key: string,
  value: string,
  defaultValue: string
): void {
  if (value === defaultValue || value.length === 0) {
    searchParams.delete(key);
  } else {
    searchParams.set(key, value);
  }
}

function readEnum<Value extends string>(
  searchParams: URLSearchParams,
  key: string,
  values: readonly Value[],
  fallback: Value
): Value {
  const value = searchParams.get(key);
  return values.includes(value as Value) ? (value as Value) : fallback;
}

function readUniqueValues(searchParams: URLSearchParams, key: string) {
  return [...new Set(searchParams.getAll(key).filter(Boolean))];
}

export type CustomAppsUrlStatus = 'all' | 'published' | 'draft' | 'trash';

export interface CustomAppsUrlFilterState {
  searchQuery: string;
  status: CustomAppsUrlStatus;
}

const CUSTOM_APP_STATUSES = ['all', 'published', 'draft', 'trash'] as const;

export const customAppsUrlFilterCodec: PageUrlFilterCodec<CustomAppsUrlFilterState> =
  {
    read(searchParams) {
      return {
        searchQuery: searchParams.get(PAGE_URL_FILTER_PARAMS.search) ?? '',
        status: readEnum(
          searchParams,
          PAGE_URL_FILTER_PARAMS.status,
          CUSTOM_APP_STATUSES,
          'all'
        ),
      };
    },
    write(searchParams, state) {
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.search,
        state.searchQuery,
        ''
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.status,
        state.status,
        'all'
      );
    },
  };

export type WorkflowUrlStatus = 'all' | 'active' | 'inactive';

export interface WorkflowUrlFilterState {
  searchQuery: string;
  status: WorkflowUrlStatus;
  categoryIds: string[];
}

const WORKFLOW_STATUSES = ['all', 'active', 'inactive'] as const;

export const workflowUrlFilterCodec: PageUrlFilterCodec<WorkflowUrlFilterState> =
  {
    read(searchParams) {
      return {
        searchQuery: searchParams.get(PAGE_URL_FILTER_PARAMS.search) ?? '',
        status: readEnum(
          searchParams,
          PAGE_URL_FILTER_PARAMS.status,
          WORKFLOW_STATUSES,
          'all'
        ),
        categoryIds: readUniqueValues(
          searchParams,
          PAGE_URL_FILTER_PARAMS.category
        ),
      };
    },
    write(searchParams, state) {
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.search,
        state.searchQuery,
        ''
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.status,
        state.status,
        'all'
      );
      searchParams.delete(PAGE_URL_FILTER_PARAMS.category);
      for (const categoryId of [
        ...new Set(state.categoryIds.filter(Boolean)),
      ]) {
        searchParams.append(PAGE_URL_FILTER_PARAMS.category, categoryId);
      }
    },
  };

export type MarketplaceUrlType =
  | 'all'
  | 'app'
  | 'app_block'
  | 'agent'
  | 'doc'
  | 'step'
  | 'workflow'
  | 'automation';
export type MarketplaceUrlSort = 'type' | 'downloads' | 'rating' | 'newest';
export type MarketplaceUrlInstallation = 'all' | 'installed' | 'not-installed';
export type MarketplaceUrlProvenance =
  | 'all'
  | 'streamlain_built'
  | 'streamlain_certified'
  | 'community_built';

export interface MarketplaceUrlFilterState {
  search: string;
  templateTypeTab: MarketplaceUrlType;
  selectedCategory?: string;
  sortBy: MarketplaceUrlSort;
  installationFilter: MarketplaceUrlInstallation;
  provenanceFilter: MarketplaceUrlProvenance;
  currentPage: number;
}

const MARKETPLACE_TYPES = [
  'all',
  'app',
  'app_block',
  'agent',
  'doc',
  'step',
  'workflow',
  'automation',
] as const;
const MARKETPLACE_SORTS = ['type', 'downloads', 'rating', 'newest'] as const;
const MARKETPLACE_INSTALLATION_FILTERS = [
  'all',
  'installed',
  'not-installed',
] as const;
const MARKETPLACE_PROVENANCE_FILTERS = [
  'all',
  'streamlain_built',
  'streamlain_certified',
  'community_built',
] as const;

export const MARKETPLACE_URL_FILTER_FALLBACK: MarketplaceUrlFilterState = {
  search: '',
  templateTypeTab: 'all',
  selectedCategory: undefined,
  sortBy: 'type',
  installationFilter: 'all',
  provenanceFilter: 'all',
  currentPage: 1,
};

export const marketplaceUrlFilterCodec: PageUrlFilterCodec<MarketplaceUrlFilterState> =
  {
    read(searchParams) {
      const rawPage = Number(searchParams.get(PAGE_URL_FILTER_PARAMS.page));
      return {
        search: searchParams.get(PAGE_URL_FILTER_PARAMS.search) ?? '',
        templateTypeTab: readEnum(
          searchParams,
          PAGE_URL_FILTER_PARAMS.type,
          MARKETPLACE_TYPES,
          'all'
        ),
        selectedCategory:
          searchParams.get(PAGE_URL_FILTER_PARAMS.category) || undefined,
        sortBy: readEnum(
          searchParams,
          PAGE_URL_FILTER_PARAMS.sort,
          MARKETPLACE_SORTS,
          'type'
        ),
        installationFilter: readEnum(
          searchParams,
          PAGE_URL_FILTER_PARAMS.installation,
          MARKETPLACE_INSTALLATION_FILTERS,
          'all'
        ),
        provenanceFilter: readEnum(
          searchParams,
          PAGE_URL_FILTER_PARAMS.provenance,
          MARKETPLACE_PROVENANCE_FILTERS,
          'all'
        ),
        currentPage: Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1,
      };
    },
    write(searchParams, state) {
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.search,
        state.search,
        ''
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.type,
        state.templateTypeTab,
        'all'
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.category,
        state.selectedCategory ?? '',
        ''
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.sort,
        state.sortBy,
        'type'
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.installation,
        state.installationFilter,
        'all'
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.provenance,
        state.provenanceFilter,
        'all'
      );
      setDefaultedParam(
        searchParams,
        PAGE_URL_FILTER_PARAMS.page,
        String(state.currentPage),
        '1'
      );
    },
  };
